import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL;
const GET_ADS_URL = `${API_URL}/Custom/GetSwapAds`;
const CREATE_AD_URL = `${API_URL}/Custom/CreateSwapAd`;
const ELIGIBLE_URL = `${API_URL}/Custom/GetSwapAdEligibleSlots`;
const ACCEPT_URL = `${API_URL}/Custom/AcceptSwapAd`;

const getSlotDateValue = slot => {
  if (!slot) return '';
  if (slot.slotDate) return slot.slotDate;
  if (slot.exactDate) return slot.exactDate.split('T')[0];
  return '';
};

const sortSlotsByDateTime = list =>
  [...list].sort((a, b) => {
    const aValue = new Date(`${getSlotDateValue(a) || ''}T${a.startTime || '00:00'}`).getTime();
    const bValue = new Date(`${getSlotDateValue(b) || ''}T${b.startTime || '00:00'}`).getTime();
    if (Number.isNaN(aValue) && Number.isNaN(bValue)) return 0;
    if (Number.isNaN(aValue)) return 1;
    if (Number.isNaN(bValue)) return -1;
    return aValue - bValue;
  });

export default function SwapAdBoard({ dutySchedule = [], timeSlots = [], onUpdated = () => {} }) {
  const [ads, setAds] = useState({ available: [], mine: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [desiredSlotIds, setDesiredSlotIds] = useState([]);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [acceptModal, setAcceptModal] = useState({ show: false, ad: null });
  const [eligibleSlots, setEligibleSlots] = useState([]);
  const [selectedTargetSlotId, setSelectedTargetSlotId] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);
  const [slotFilterTerm, setSlotFilterTerm] = useState('');
  const [showConflicting, setShowConflicting] = useState(false);
  const orderedEligibleSlots = useMemo(() => sortSlotsByDateTime(eligibleSlots), [eligibleSlots]);

  const advertisedSlotIds = useMemo(() => {
    const mine = Array.isArray(ads.mine) ? ads.mine : [];
    const ids = mine.map(ad => ad.slotId).filter(Boolean);
    return new Set(ids);
  }, [ads.mine]);

  const slotLookup = useMemo(() => {
    const lookup = {};
    timeSlots.forEach(ts => {
      lookup[ts.id] = ts;
    });
    return lookup;
  }, [timeSlots]);

  const toSortValue = slot => {
    if (!slot || !slot.exactDate) return Number.MAX_SAFE_INTEGER;
    const dateOnly = slot.exactDate.split('T')[0];
    const start = slot.startTime ? slot.startTime.substring(0, 5) : '00:00';
    return new Date(`${dateOnly}T${start}:00`).getTime();
  };

  const renderSlotLabel = slot => {
    const date = getSlotDateValue(slot);
    const start = slot?.startTime ? slot.startTime.substring(0, 5) : '';
    const end = slot?.endTime ? slot.endTime.substring(0, 5) : '';
    const parts = [date, start || end ? `${start}${end ? ` – ${end}` : ''}` : ''].filter(Boolean);
    return parts.join(' | ');
  };

  const slotFilterOptions = useMemo(() => {
    const seen = new Set();
    return sortSlotsByDateTime(timeSlots)
      .map(slot => renderSlotLabel(slot))
      .filter(label => label && !seen.has(label) && seen.add(label))
      .map((label, index) => ({ id: `${index}-${label}`, label }));
  }, [timeSlots]);

  const availableDutySlots = useMemo(() => {
    const unique = {};
    dutySchedule
      .filter(d => d.slotId && !d.isOff)
      .forEach(d => {
        if (!unique[d.slotId]) {
          const slot = slotLookup[d.slotId] || {};
          const dateLabel = slot.exactDate
            ? new Date(slot.exactDate).toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : 'Tarih bilgisi yok';
          const start = slot.startTime ? slot.startTime.substring(0, 5) : '';
          const end = slot.endTime ? slot.endTime.substring(0, 5) : '';
          unique[d.slotId] = {
            slotId: d.slotId,
            isAdvertised: advertisedSlotIds.has(d.slotId),
            sortValue: toSortValue(slot),
            label: `${dateLabel} | ${start} – ${end} ${d.roleName ? `| ${d.roleName}` : ''} ${
              d.examName ? `| ${d.examName}` : ''
            }`.trim(),
          };
        }
      });
    return Object.values(unique).sort((a, b) => a.sortValue - b.sortValue || a.label.localeCompare(b.label, 'tr'));
  }, [advertisedSlotIds, dutySchedule, slotLookup]);

  const myAssignedSlotIds = useMemo(
    () => new Set(dutySchedule.filter(d => d.slotId && !d.isOff).map(d => d.slotId)),
    [dutySchedule]
  );
  const myOffSlotIds = useMemo(
    () => new Set(dutySchedule.filter(d => d.slotId && d.isOff).map(d => d.slotId)),
    [dutySchedule]
  );

  const desiredSlotOptions = useMemo(() => {
    return timeSlots
      .filter(ts => ts.id)
      .filter(ts => !myAssignedSlotIds.has(ts.id) && !myOffSlotIds.has(ts.id) && ts.id !== Number(selectedSlotId))
      .map(ts => {
        const dateLabel = ts.exactDate
          ? new Date(ts.exactDate).toLocaleDateString('tr-TR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          : 'Tarih bilgisi yok';
        const start = ts.startTime ? ts.startTime.substring(0, 5) : '';
        const end = ts.endTime ? ts.endTime.substring(0, 5) : '';
        const sortValue = toSortValue(ts);
        return {
          id: ts.id,
          label: `${dateLabel} | ${start} – ${end}`,
          sortValue,
          slotDate: ts.exactDate ? ts.exactDate.split('T')[0] : '',
          startTime: start,
          endTime: end,
        };
      })
      .sort((a, b) => a.sortValue - b.sortValue || a.label.localeCompare(b.label, 'tr'));
  }, [myAssignedSlotIds, myOffSlotIds, selectedSlotId, timeSlots]);

  const loadAds = () => {
    setIsLoading(true);
    axios
      .get(GET_ADS_URL)
      .then(({ data }) => setAds(data || { available: [], mine: [] }))
      .catch(err => {
        console.error(err);
        Swal.fire({ title: 'Hata', text: 'İlanlar yüklenirken bir sorun oluştu.', icon: 'error' });
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadAds();
  }, []);

  const toggleDesiredSlot = id => {
    setDesiredSlotIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    setDesiredSlotIds(prev => prev.filter(id => desiredSlotOptions.some(opt => opt.id === id)));
  }, [desiredSlotOptions]);

  useEffect(() => {
    if (!selectedSlotId) return;
    setDesiredSlotIds(prev => prev.filter(id => id !== Number(selectedSlotId)));
  }, [selectedSlotId]);

  useEffect(() => {
    if (selectedSlotId && advertisedSlotIds.has(Number(selectedSlotId))) {
      setSelectedSlotId('');
    }
  }, [advertisedSlotIds, selectedSlotId]);

  const handleCreate = () => {
    if (!selectedSlotId) {
      Swal.fire({ title: 'Uyarı', text: 'Lütfen bir görev seçin.', icon: 'warning' });
      return;
    }
    if (!desiredSlotIds.length) {
      Swal.fire({
        title: 'Uyarı',
        text: 'Değişim yapmak istediğiniz gün ve saatleri seçmelisiniz.',
        icon: 'warning',
      });
      return;
    }
    setIsSaving(true);
    axios
      .post(CREATE_AD_URL, { slotId: Number(selectedSlotId), note, desiredSlotIds })
      .then(() => {
        Swal.fire({ title: 'İlan Yayında', text: 'İlanınız başarıyla oluşturuldu.', icon: 'success' });
        setSelectedSlotId('');
        setDesiredSlotIds([]);
        setNote('');
        setShowCreate(false);
        loadAds();
        onUpdated();
      })
      .catch(err => {
        console.error(err);
        const message = err?.response?.data?.message || err?.response?.data || 'İlan oluşturulurken bir hata oluştu.';
        Swal.fire({ title: 'Hata', text: message, icon: 'error' });
      })
      .finally(() => setIsSaving(false));
  };

  const openAcceptModal = ad => {
    setAcceptModal({ show: true, ad });
    setEligibleSlots([]);
    setSelectedTargetSlotId('');
    axios
      .get(`${ELIGIBLE_URL}?adId=${ad.id}`)
      .then(({ data }) => {
        const sorted = sortSlotsByDateTime(data || []);
        setEligibleSlots(sorted);
        if (sorted.length > 0) {
          setSelectedTargetSlotId(sorted[0].id);
        }
      })
      .catch(err => {
        console.error(err);
        Swal.fire({ title: 'Hata', text: 'Uygun görevler alınamadı.', icon: 'error' });
        setAcceptModal({ show: false, ad: null });
      });
  };

  const handleAccept = () => {
    if (!selectedTargetSlotId) {
      Swal.fire({ title: 'Uyarı', text: 'Lütfen değişim yapmak istediğiniz görevi seçin.', icon: 'warning' });
      return;
    }
    setIsAccepting(true);
    axios
      .post(ACCEPT_URL, { adId: acceptModal.ad.id, targetSlotId: Number(selectedTargetSlotId) })
      .then(() => {
        Swal.fire({
          title: 'Değişim Tamamlandı',
          text: 'İlan kabul edildi. Yönetim onayı otomatik verilerek değişim tamamlandı ve sonuç yönetime e-posta ile bildirildi.',
          icon: 'success',
        });
        setAcceptModal({ show: false, ad: null });
        setEligibleSlots([]);
        setSelectedTargetSlotId('');
        loadAds();
        onUpdated();
      })
      .catch(err => {
        console.error(err);
        const message = err?.response?.data?.message || err?.response?.data || 'İlan kabul edilirken bir hata oluştu.';
        Swal.fire({ title: 'Hata', text: message, icon: 'error' });
      })
      .finally(() => setIsAccepting(false));
  };

  const renderAdSlot = ad => {
    return renderSlotLabel(ad);
  };

  const filteredAvailableAds = useMemo(() => {
    const term = slotFilterTerm.trim().toLowerCase();
    let list = Array.isArray(ads.available) ? ads.available : [];
    if (!showConflicting) {
      list = list.filter(ad => !ad.isConflicting);
    }
    if (!term) return list;
    return list.filter(ad => {
      const advertisedSlotLabel = renderAdSlot(ad).toLowerCase();
      const desiredMatch = Array.isArray(ad.desiredSlots)
        ? ad.desiredSlots.some(slot => renderSlotLabel(slot).toLowerCase().includes(term))
        : false;
      return advertisedSlotLabel.includes(term) || desiredMatch;
    });
  }, [ads.available, showConflicting, slotFilterTerm]);

  return (
    <div className='card mb-4'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <button
          type='button'
          className='btn btn-link text-decoration-none p-0 d-flex align-items-center'
          onClick={() => setIsCollapsed(prev => !prev)}
          aria-expanded={!isCollapsed}
          aria-controls='swap-ad-board-body'
        >
          <h5 className='mb-0 me-2 text-dark'>Görev Değişim İlanları</h5>
          <span aria-hidden='true'>{isCollapsed ? '▸' : '▾'}</span>
        </button>
        <button className='btn btn-sm btn-primary' onClick={() => setShowCreate(true)}>
          İlan Ver
        </button>
      </div>
      <div className={`collapse ${!isCollapsed ? 'show' : ''}`} id='swap-ad-board-body'>
        <div className='card-body'>
          {isLoading ? (
            <p>İlanlar yükleniyor...</p>
          ) : (
            <>
              <div className='d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between gap-2 mb-3'>
                <h6 className='fw-bold mb-0'>Yayındaki İlanlar</h6>
                <div className='d-flex align-items-center gap-2 w-100 w-lg-auto'>
                  <input
                    type='search'
                    className='form-control form-control-sm'
                    placeholder='Slot tarih/saatine göre ara'
                    value={slotFilterTerm}
                    onChange={e => setSlotFilterTerm(e.target.value)}
                    list='slot-filter-options'
                  />
                  <datalist id='slot-filter-options'>
                    {slotFilterOptions.map(opt => (
                      <option key={opt.id} value={opt.label} />
                    ))}
                  </datalist>
                  {slotFilterTerm ? (
                    <button className='btn btn-outline-secondary btn-sm' onClick={() => setSlotFilterTerm('')}>
                      Temizle
                    </button>
                  ) : null}
                  <div className='form-check mb-0 ms-lg-2'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='show-conflicting-ads'
                      checked={showConflicting}
                      onChange={e => setShowConflicting(e.target.checked)}
                    />
                    <label className='form-check-label' htmlFor='show-conflicting-ads'>
                      Uygun olmayanları göster
                    </label>
                  </div>
                </div>
              </div>
              {ads.available?.length ? (
                filteredAvailableAds.length ? (
                  <div className='row'>
                    {filteredAvailableAds.map(ad => (
                      <div className='col-md-6 mb-3' key={ad.id}>
                        <div className='border rounded p-3 h-100'>
                          <div className='d-flex justify-content-between align-items-start'>
                            <div>
                              <strong>{ad.ownerName}</strong>
                              <div className='text-muted small'>{ad.roleName}</div>
                            </div>
                            <span className='badge bg-info text-white'>{ad.classroomCode}</span>
                          </div>
                          <div className='mt-2'>
                            <div>{renderAdSlot(ad)}</div>
                            {ad.note && <div className='text-muted small mt-1'>Not: {ad.note}</div>}
                            {ad.desiredSlots?.length ? (
                              <div className='text-muted small mt-2'>
                                <div className='fw-bold text-body'>Tercih edilen zamanlar</div>
                                <ul className='mb-0 ps-3'>
                                  {sortSlotsByDateTime(ad.desiredSlots).map(slot => (
                                    <li key={slot.id}>{renderSlotLabel(slot)}</li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <div className='text-muted small mt-2'>Herhangi bir uygunluk belirtmedi.</div>
                            )}
                          </div>
                          <div className='d-flex justify-content-end mt-3'>
                            <button
                              className={`btn btn-sm ${ad.isConflicting ? 'btn-secondary' : 'btn-success'}`}
                              onClick={() => !ad.isConflicting && openAcceptModal(ad)}
                              disabled={ad.isConflicting}
                            >
                              {ad.isConflicting ? 'Uygun Değil' : 'İlanı Kabul Et'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>Filtrelerinize uyan ilan bulunamadı.</p>
                )
              ) : (
                <p>Şu anda yayında ilan bulunmuyor.</p>
              )}

              <hr />
              <h6 className='fw-bold'>İlanlarım</h6>
              {ads.mine?.length ? (
                <div className='table-responsive'>
                  <table className='table table-sm align-middle'>
                    <thead>
                      <tr>
                        <th>Görev</th>
                        <th>Sınıf</th>
                        <th>Durum</th>
                        <th>Kabul Eden</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ads.mine.map(ad => (
                        <tr key={ad.id}>
                          <td>
                            <div className='fw-bold'>{renderAdSlot(ad)}</div>
                            <div className='text-muted small'>{ad.roleName}</div>
                            {ad.desiredSlots?.length ? (
                              <div className='text-muted small mt-1'>
                                Tercih edilen:{' '}
                                {sortSlotsByDateTime(ad.desiredSlots)
                                  .map(renderSlotLabel)
                                  .join(', ')}
                              </div>
                            ) : null}
                          </td>
                          <td>{ad.classroomCode}</td>
                          <td>{ad.status}</td>
                          <td>{ad.acceptedByName || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>Henüz ilan oluşturmadınız.</p>
              )}
            </>
          )}
        </div>
      </div>

      {showCreate && (
        <div
          className='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
          style={{ backgroundColor: 'rgba(255,255,255,0.75)', zIndex: 1100 }}
        >
          <div className='modal-content' style={{ maxWidth: '520px' }}>
            <div className='modal-header'>
              <h5 className='modal-title'>Yeni İlan</h5>
              <button type='button' className='btn-close' onClick={() => setShowCreate(false)}></button>
            </div>
            <div className='modal-body'>
              <div className='alert alert-warning' role='alert'>
                İlan yayımlandıktan sonra silinemez veya düzenlenemez. Lütfen bilgileri kontrol ederek ilerleyin.
              </div>
              <div className='mb-3'>
                <label className='form-label'>Görev</label>
                <select
                  className='form-select'
                  value={selectedSlotId}
                  onChange={e => setSelectedSlotId(e.target.value)}
                >
                  <option value=''>-- Görev Seçin --</option>
                  {availableDutySlots.map(opt => {
                    const disabled = opt.isAdvertised;
                    return (
                      <option key={opt.slotId} value={opt.slotId} disabled={disabled}>
                        {opt.label} {disabled ? '(daha önce ilan verilmiş)' : ''}
                      </option>
                    );
                  })}
                </select>
                {availableDutySlots.some(opt => opt.isAdvertised) ? (
                  <div className='form-text text-danger mt-1'>
                    Daha önce ilan verilmiş görevler yeniden seçilemez.
                  </div>
                ) : null}
              </div>
              <div className='mb-3'>
                <label className='form-label'>Talep Ettiğiniz Gün ve Saatler</label>
                <div className='border rounded p-2' style={{ maxHeight: '240px', overflowY: 'auto' }}>
                  {desiredSlotOptions.length ? (
                    desiredSlotOptions.map(opt => (
                      <div className='form-check' key={opt.id}>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          value={opt.id}
                          id={`desired-${opt.id}`}
                          checked={desiredSlotIds.includes(opt.id)}
                          onChange={() => toggleDesiredSlot(opt.id)}
                        />
                        <label className='form-check-label' htmlFor={`desired-${opt.id}`}>
                          {opt.label}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className='text-muted mb-0'>Müsait zaman dilimi bulunamadı.</p>
                  )}
                </div>
                <div className='form-text'>
                  Sadece seçtiğiniz zaman dilimlerinde uygun olan kişiler ilanı kabul edebilir.
                </div>
              </div>
              <div className='mb-3'>
                <label className='form-label'>Not (İsteğe bağlı)</label>
                <textarea
                  className='form-control'
                  rows={3}
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder='Kısa not ekleyebilirsiniz.'
                />
              </div>
            </div>
            <div className='modal-footer'>
              <button className='btn btn-secondary' onClick={() => setShowCreate(false)}>
                Vazgeç
              </button>
              <button className='btn btn-primary' onClick={handleCreate} disabled={isSaving}>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {acceptModal.show && (
        <div
          className='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
          style={{ backgroundColor: 'rgba(255,255,255,0.75)', zIndex: 1100 }}
        >
          <div className='modal-content' style={{ maxWidth: '540px' }}>
            <div className='modal-header'>
              <h5 className='modal-title'>İlanı Kabul Et</h5>
              <button type='button' className='btn-close' onClick={() => setAcceptModal({ show: false, ad: null })}></button>
            </div>
            <div className='modal-body'>
              <div className='mb-3'>
                <strong>{acceptModal.ad?.ownerName}</strong>
                <div className='text-muted small mt-1'>
                  {renderAdSlot(acceptModal.ad || {})} | {acceptModal.ad?.classroomCode}
                </div>
                {acceptModal.ad?.note && <div className='mt-2'>Not: {acceptModal.ad.note}</div>}
                {acceptModal.ad?.desiredSlots?.length ? (
                  <div className='text-muted small mt-2'>
                    <div className='fw-bold text-body'>Talep edilen zaman dilimleri</div>
                    <ul className='mb-0 ps-3'>
                      {sortSlotsByDateTime(acceptModal.ad.desiredSlots).map(slot => (
                        <li key={slot.id}>{renderSlotLabel(slot)}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              <div className='mb-2'>
                <label className='form-label'>Değişim yapmak istediğiniz görevi seçin</label>
                {orderedEligibleSlots.length ? (
                  orderedEligibleSlots.map(slot => (
                    <div className='form-check' key={slot.id}>
                      <input
                        className='form-check-input'
                        type='radio'
                        name='eligible-slot'
                        id={`eligible-${slot.id}`}
                        value={slot.id}
                        checked={Number(selectedTargetSlotId) === slot.id}
                        onChange={() => setSelectedTargetSlotId(slot.id)}
                      />
                      <label className='form-check-label' htmlFor={`eligible-${slot.id}`}>
                        {slot.slotDate} | {slot.startTime.substring(0, 5)} – {slot.endTime.substring(0, 5)} |{' '}
                        {slot.classroomCode}
                      </label>
                    </div>
                  ))
                ) : (
                  <p className='text-muted'>Uygun göreviniz bulunmuyor.</p>
                )}
              </div>
            </div>
            <div className='modal-footer'>
              <button className='btn btn-secondary' onClick={() => setAcceptModal({ show: false, ad: null })}>
                Vazgeç
              </button>
              <button
                className='btn btn-success'
                onClick={handleAccept}
                disabled={!selectedTargetSlotId || !eligibleSlots.length || isAccepting}
              >
                Kabul Et
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

SwapAdBoard.propTypes = {
  dutySchedule: PropTypes.array,
  timeSlots: PropTypes.array,
  onUpdated: PropTypes.func,
};

SwapAdBoard.defaultProps = {
  dutySchedule: [],
  timeSlots: [],
  onUpdated: () => {},
};
